/**
 * Read the source workbook into raw rows.
 *
 * The parsing lives here rather than in `core` because it needs a file reader; the
 * planning logic that acts on these rows is pure and tested separately.
 *
 * exceljs is loaded dynamically — it is a large library used on exactly one screen, and
 * shipping it in the main bundle would slow every page load for a file the user opens
 * once (AD-06).
 */

import type { RawRow } from '../../core/import';

/** The sheets that hold the transaction ledger. `Credits` is a different shape. */
const LEDGER_SHEETS = [
  'January 2026', 'February 2026', 'March 2026', 'April 2026',
  'May 2026', 'June 2026', 'July 2026', 'August 2026',
];

/** Ledger columns: A account, B description, C amount, D date. No header row. */
const FIRST_DATA_ROW = 2;
const LAST_DATA_ROW = 200;

export class WorkbookError extends Error {}

function cellText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  // exceljs returns rich text and formula objects rather than plain values.
  const rich = value as { richText?: { text: string }[]; text?: string; result?: unknown };
  if (rich.richText) return rich.richText.map((r) => r.text).join('').trim() || null;
  if (typeof rich.text === 'string') return rich.text.trim() || null;
  return null;
}

/**
 * Amount cell, which may be a number, a formula, or text.
 *
 * ~41 of them hold arithmetic expressions like `18+64+48`. exceljs exposes those as
 * `{ formula, result }` — the **formula** is what gets kept, because FR-TXN-08 requires
 * storing what the user actually wrote alongside the evaluated figure.
 */
function amountCell(value: unknown): string | number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  const formula = value as { formula?: string; result?: unknown };
  if (typeof formula.formula === 'string') {
    // Leading "=" and "+" are Excel syntax, not arithmetic.
    return formula.formula.replace(/^[=+]/, '');
  }
  if (typeof value === 'string') return value.trim() || null;
  if (typeof formula.result === 'number') return formula.result;
  return null;
}

/** Date cell → Excel serial, which is what the planner expects. */
function dateSerial(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) {
    return Math.round((value.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000);
  }
  const formula = value as { result?: unknown };
  if (typeof formula.result === 'number') return formula.result;
  if (formula.result instanceof Date) {
    return Math.round((formula.result.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000);
  }
  return null;
}

export interface ParsedWorkbook {
  rows: RawRow[];
  sheetsRead: string[];
  sheetsSkipped: string[];
}

export async function parseWorkbook(file: File): Promise<ParsedWorkbook> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  try {
    await workbook.xlsx.load(await file.arrayBuffer());
  } catch {
    throw new WorkbookError('That file could not be read as an .xlsx workbook.');
  }

  const rows: RawRow[] = [];
  const sheetsRead: string[] = [];
  const sheetsSkipped: string[] = [];

  for (const worksheet of workbook.worksheets) {
    const name = worksheet.name.trim();
    if (!LEDGER_SHEETS.includes(name)) {
      sheetsSkipped.push(name);
      continue;
    }
    sheetsRead.push(name);
    // The sheet is named "January 2026"; rows are keyed by the month word alone.
    const sheetKey = name.split(' ')[0];

    for (let rowNumber = FIRST_DATA_ROW; rowNumber <= LAST_DATA_ROW; rowNumber += 1) {
      const row = worksheet.getRow(rowNumber);
      const description = cellText(row.getCell(2).value);
      const amount = amountCell(row.getCell(3).value);

      // A row is a transaction only if it has both a description and an amount. The
      // grid holds five other blocks; this is what keeps them out.
      if (description === null || amount === null) continue;

      rows.push({
        sheet: sheetKey,
        rowNumber,
        account: cellText(row.getCell(1).value),
        description,
        amount,
        dateSerial: dateSerial(row.getCell(4).value),
      });
    }
  }

  if (rows.length === 0) {
    throw new WorkbookError(
      `No ledger rows found. Expected sheets named ${LEDGER_SHEETS.slice(0, 2).join(', ')}, and so on.`,
    );
  }

  return { rows, sheetsRead, sheetsSkipped };
}
